import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, compressImage, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, MapPin, X, CheckCircle2 } from "lucide-react";

export default function ConclusionDialog({ open, onOpenChange, occId, evidence, onDone }) {
  const [photos, setPhotos] = useState([]);
  const [description, setDescription] = useState("");
  const [signature, setSignature] = useState("");
  const [gps, setGps] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const addFiles = async (files) => {
    const list = Array.from(files).slice(0, 20 - photos.length);
    const compressed = await Promise.all(list.map((f) => compressImage(f)));
    setPhotos((p) => [...p, ...compressed]);
  };

  const captureGps = () => {
    if (!navigator.geolocation) return toast.error("GPS não disponível neste dispositivo");
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) }),
      () => toast.error("Não foi possível obter a localização")
    );
  };

  const submit = async () => {
    if (photos.length < evidence.min_photos) return toast.error(`Adicione no mínimo ${evidence.min_photos} foto(s) de evidência`);
    if (evidence.require_comment && description.trim().length < 5) return toast.error("Descreva a ação corretiva executada");
    if (evidence.require_signature && !signature.trim()) return toast.error("Assinatura do responsável é obrigatória");
    if (evidence.require_gps && !gps) return toast.error("Localização GPS é obrigatória");
    setSending(true);
    try {
      await api.post(`/occurrences/${occId}/request-conclusion`, { photos, description, signature, gps });
      toast.success("Conclusão solicitada! Aguardando aprovação do supervisor.");
      onOpenChange(false);
      setPhotos([]); setDescription(""); setSignature(""); setGps(null);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="conclusion-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Solicitar Conclusão</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Registre as evidências da correção. Um supervisor irá analisar e aprovar a conclusão.
          </p>
          <div className="space-y-2">
            <Label>Fotos da correção * (mín. {evidence.min_photos})</Label>
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="conclusion-add-photo-btn">
                <Camera size={14} className="mr-1.5" /> Adicionar fotos
              </Button>
              <span className="text-xs text-muted-foreground">{photos.length}/20</span>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="conclusion-file-input" />
            {photos.length > 0 && (
              <div className="grid grid-cols-5 gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative group aspect-square rounded-md overflow-hidden border border-border">
                    <img src={p} alt={`Evidência ${i + 1}`} className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                      className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Ação corretiva executada {evidence.require_comment ? "*" : "(opcional)"}</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o que foi feito para corrigir a ocorrência..." className="resize-none" data-testid="conclusion-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Assinatura {evidence.require_signature ? "*" : "(opcional)"}</Label>
              <Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Nome do responsável" data-testid="conclusion-signature-input" />
            </div>
            <div className="space-y-2">
              <Label>GPS {evidence.require_gps ? "*" : "(opcional)"}</Label>
              <Button type="button" variant="outline" className="w-full" onClick={captureGps} data-testid="conclusion-gps-btn">
                <MapPin size={14} className="mr-1.5" />
                {gps ? `${gps.lat}, ${gps.lng}` : "Capturar localização"}
              </Button>
            </div>
          </div>
          <Button className="w-full" onClick={submit} disabled={sending} data-testid="conclusion-submit-btn">
            <CheckCircle2 size={15} className="mr-2" />
            {sending ? "Enviando..." : "Solicitar Conclusão"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
